import { useEffect, useState, useMemo } from "react";
import { toast } from "sonner";
import { api, fmtKES, fmtPct } from "@/lib/api";
import { CurrencyValue } from "@/components/CurrencyValue";
import { Progress } from "@/components/ui/progress";
import { signTone, toneClassName, signedPrefix } from "@/lib/numberColor";
import InfoTooltip from "@/components/InfoTooltip";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Download } from "lucide-react";
import { useMarketSocket } from "@/lib/ws";
import DailyResetChip from "@/components/dashboard/DailyResetChip";
import "@/components/dashboard/obondo-interrupts.css";
import SkeletonGrid from "@/components/SkeletonGrid";
import CopyButton from "@/components/CopyButton";
import ProfileCompletionWidget from "@/components/dashboard/ProfileCompletionWidget";
import InvestmentStrategyNudge from "@/components/dashboard/InvestmentStrategyNudge";
import RecentActivityFeed from "@/components/dashboard/RecentActivityFeed";

import ProfitTargetEditor from "@/components/dashboard/ProfitTargetEditor";
import DrawdownEditor from "@/components/dashboard/DrawdownEditor";
import ClosedTradesCard from "@/components/dashboard/ClosedTradesCard";
import AfricanPortfolioMap from "@/components/dashboard/AfricanPortfolioMap";
import DashboardOpenPositions from "@/components/dashboard/DashboardOpenPositions";
import SelfUnfreezeModal from "@/components/dashboard/SelfUnfreezeModal";
import PsychometricBaselineNudge from "@/components/dashboard/PsychometricBaselineNudge";
import DebriefInsightsCard from "@/components/dashboard/DebriefInsightsCard";
import ObondoTrialBanner from "@/components/dashboard/ObondoTrialBanner";
import BreakdownPatternBanner from "@/components/beco/BreakdownPatternBanner";
import StripeCheckoutModal from "@/components/subscription/StripeCheckoutModal";
import PageHeader from "@/components/dashboard/PageHeader";
import StatCard, { StatusBadge } from "@/components/dashboard/StatCard";
import DashboardGridWrapper from "@/components/dashboard/DashboardGridWrapper";
import { useAppCopy } from "@/lib/app-i18n";
import { useDisplayCurrency, formatDisplayAmount } from "@/lib/displayCurrency";
import { useOnboardingGate } from "@/context/OnboardingGateContext";
import { Lock } from "lucide-react";

// ---- Legacy Stat wrapper: keeps behavioural back-compat for a few callsites
// that pass tooltip props. New callsites should use <StatCard>.
function Stat({ label, value, sub, tone = "default", tooltipTerm, tooltipBody, testId }) {
    return (
        <StatCard
            label={label}
            value={value}
            caption={sub}
            testId={testId}
            action={tooltipTerm ? <InfoTooltip term={tooltipTerm}>{tooltipBody}</InfoTooltip> : null}
            trend={tone === "up" ? "up" : tone === "down" ? "down" : undefined}
            trendText={tone === "up" || tone === "down" ? " " : undefined}
            dense
        />
    );
}

/* ── KpiSortableRow ──────────────────────────────────────────────
 * Round 66: 4 above-the-fold KPI cards, reorderable via dnd-kit.
 * Order is persisted per-user in localStorage so every trader can pin
 * their favourite metric to the top-left. The default order matches
 * the historical layout so no existing user notices a jump on first
 * render.
 * ---------------------------------------------------------------- */
/* Sept 2026 — 8 permanent, non-reorderable KPI tiles (previously 4,
 * drag-to-reorder). BeCo Index, Behavioural Baseline, Sharpe Ratio and a
 * combined Behavioural Alpha+Sigma tile were promoted from the removable
 * widget grid below into this fixed row so every trader sees all 8 the
 * instant the dashboard loads. */
const KPI_FIXED_ORDER = [
    "stat-equity", "stat-floating", "stat-total-pnl", "stat-win-rate",
    "stat-beco-index", "stat-behavioral-baseline", "stat-sharpe", "stat-alpha-sigma",
];

/* Compact locked-tile fallback for the behavioural slots — free/lite tier
 * users still see exactly 8 tiles (never fewer), just with a lock instead
 * of a live figure. */
function LockedMiniStat({ label, testId }) {
    return (
        <StatCard
            label={label}
            value={<span className="text-muted-foreground">—</span>}
            caption={
                <Link
                    to="/pricing"
                    className="inline-flex items-center gap-1 text-primary hover:underline"
                    data-testid={`${testId}-upgrade-link`}
                >
                    <Lock className="w-3 h-3" strokeWidth={1.75} /> Upgrade to unlock
                </Link>
            }
            testId={testId}
        />
    );
}

function KpiSortableRow({ userId, metrics, fmt, display, isInstitutional, onReload }) {
    const [beco, setBeco] = useState({ loading: true, gated: false, data: null });
    const [baseline, setBaseline] = useState({ loading: true, gated: false, data: null });
    const [alpha, setAlpha] = useState({ loading: true, gated: false, data: null });

    useEffect(() => {
        let cancelled = false;
        api.post("/beco/compute").then(({ data }) => {
            if (!cancelled) setBeco({ loading: false, gated: false, data });
        }).catch((e) => {
            if (cancelled) return;
            setBeco({ loading: false, gated: e?.response?.status === 402, data: null });
        });
        api.get("/psychometric/mine").then(({ data }) => {
            if (!cancelled) setBaseline({ loading: false, gated: false, data });
        }).catch((e) => {
            if (cancelled) return;
            setBaseline({ loading: false, gated: e?.response?.status === 402, data: null });
        });
        api.get("/behavioral-alpha/mine").then(({ data }) => {
            if (!cancelled) setAlpha({ loading: false, gated: false, data });
        }).catch((e) => {
            if (cancelled) return;
            setAlpha({ loading: false, gated: e?.response?.status === 402, data: null });
        });
        return () => { cancelled = true; };
    }, [userId]);

    const sharpeMinDays = metrics.sharpe_min_days || 5;
    const sharpeInsufficient = (metrics.unique_trading_days || 0) < sharpeMinDays;
    const sharpeVal = Number(metrics.sharpe_ratio || 0);

    const cards = {
        "stat-equity": (
            <StatCard
                label={display.code === "KES" ? "Equity" : `Equity (${display.code})`}
                value={<CurrencyValue amount={metrics.equity} testId="stat-equity-val" />}
                caption={<span data-testid="stat-equity-display-currency">Bal: <CurrencyValue amount={metrics.current_balance} showToggle={false} /></span>}
                testId="stat-equity"
            />
        ),
        "stat-floating": (
            <StatCard
                label="Floating PnL"
                value={<CurrencyValue amount={metrics.floating_pnl} testId="stat-floating-val" />}
                trend={metrics.floating_pnl >= 0 ? "up" : "down"}
                trendText={metrics.floating_pnl >= 0 ? "Positive" : "Negative"}
                testId="stat-floating"
            />
        ),
        "stat-total-pnl": (
            <StatCard
                label="Net PnL"
                value={<CurrencyValue amount={metrics.total_pnl} testId="stat-total-pnl-val" />}
                trend={metrics.total_pnl >= 0 ? "up" : "down"}
                trendText={metrics.total_pnl >= 0 ? "Positive" : "Negative"}
                caption={`${metrics.total_trades} closed`}
                testId="stat-total-pnl"
            />
        ),
        "stat-win-rate": (
            <StatCard
                label="Win rate"
                value={fmtPct(metrics.win_rate)}
                caption={`${metrics.wins}W · ${metrics.losses}L`}
                testId="stat-win-rate"
            />
        ),

        "stat-beco-index": beco.gated ? (
            <LockedMiniStat label="BeCo Index" testId="stat-beco-index" />
        ) : beco.loading ? (
            <StatCard label="BeCo Index" value="…" testId="stat-beco-index" />
        ) : (!beco.data || beco.data.insufficient_data) ? (
            <StatCard
                label="BeCo Index"
                value={<span className="text-muted-foreground">—</span>}
                caption={`${beco.data?.n_30d ?? 0} trades so far · needs 3+`}
                testId="stat-beco-index"
            />
        ) : (
            <StatCard
                label="BeCo Index"
                value={`${Number(beco.data.beco_index).toFixed(1)}`}
                caption="/ 100 · 30d rolling"
                trend={beco.data.beco_index >= 60 ? "up" : beco.data.beco_index >= 35 ? "neutral" : "down"}
                trendText=" "
                testId="stat-beco-index"
            />
        ),

        "stat-behavioral-baseline": baseline.gated ? (
            <LockedMiniStat label="Behavioural Baseline" testId="stat-behavioral-baseline" />
        ) : baseline.loading ? (
            <StatCard label="Behavioural Baseline" value="…" testId="stat-behavioral-baseline" />
        ) : !baseline.data?.has_baseline ? (
            <StatCard
                label="Behavioural Baseline"
                value={<span className="text-muted-foreground">—</span>}
                caption={
                    <Link to="/onboarding/psychometric" className="text-primary hover:underline" data-testid="stat-behavioral-baseline-cta">
                        Take the baseline →
                    </Link>
                }
                testId="stat-behavioral-baseline"
            />
        ) : (
            <StatCard
                label="Behavioural Baseline"
                value={`${baseline.data.composite_score}/100`}
                caption={String(baseline.data.behavioural_bucket || "").replace(/_/g, " ")}
                testId="stat-behavioral-baseline"
            />
        ),

        "stat-sharpe": (
            <StatCard
                label="Sharpe Ratio"
                value={sharpeInsufficient ? <span className="text-muted-foreground">—</span> : sharpeVal.toFixed(2)}
                caption={sharpeInsufficient ? `Needs ${sharpeMinDays}+ trading days` : "Excess return / risk, annualized"}
                trend={sharpeInsufficient ? "neutral" : sharpeVal >= 1 ? "up" : sharpeVal >= 0 ? "neutral" : "down"}
                trendText={sharpeInsufficient ? undefined : " "}
                testId="stat-sharpe"
            />
        ),

        "stat-alpha-sigma": (beco.gated || alpha.gated) ? (
            <LockedMiniStat label="Behavioural Alpha & Sigma" testId="stat-alpha-sigma" />
        ) : (beco.loading || alpha.loading) ? (
            <StatCard label="Behavioural Alpha & Sigma" value="…" testId="stat-alpha-sigma" />
        ) : (
            <StatCard
                label="Behavioural Alpha & Sigma"
                value={
                    <div className="flex items-center gap-4" data-testid="stat-alpha-sigma-val">
                        <span className="inline-flex items-baseline gap-1" title="Behavioural Alpha — standardized score of your trade alpha vs. your own history (not a KES amount)">
                            <span className="text-muted-foreground text-xs">α</span>
                            {alpha.data?.alpha_score_insufficient
                                ? <span className="text-muted-foreground">—</span>
                                : <span className={toneClassName(signTone(alpha.data?.alpha_score ?? 0))}>{signedPrefix(alpha.data?.alpha_score ?? 0)}{Number(alpha.data?.alpha_score ?? 0).toFixed(2)}</span>}
                        </span>
                        <span className="inline-flex items-baseline gap-1" title="Behavioural Sigma — risk-discipline score (lower is better)">
                            <span className="text-muted-foreground text-xs">σ</span>
                            {beco.data ? Number(beco.data.sigma_raw ?? 0).toFixed(2) : "—"}
                        </span>
                    </div>
                }
                caption={alpha.data?.alpha_score_insufficient ? "α needs 3+ counterfactual trades · σ: risk discipline" : "α: standardized alpha score · σ: risk discipline (lower better)"}
                testId="stat-alpha-sigma"
            />
        ),
    };

    return (
        <div className="mb-3" data-testid="dashboard-kpi-section">
            <div
                className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3"
                data-testid="dashboard-kpi-row"
            >
                {KPI_FIXED_ORDER.map((id) => (
                    <div key={id} data-testid={`kpi-tile-${id}`}>{cards[id]}</div>
                ))}
            </div>
        </div>
    );
}

export default function Dashboard() {
    const { user } = useAuth();
    const { t: tApp } = useAppCopy();
    const { stage } = useOnboardingGate();
    const isInstitutional = Boolean(
        user?.role === "broker"
        || user?.tier_source === "agency_bundled"
        || user?.agency_id
        || user?.tier === "advanced"
        || user?.tier === "moe_institution"
        || user?.tier === "cma_institution"
    );
    const [metrics, setMetrics] = useState(null);
    const [tickers, setTickers] = useState([]);
    const [geoHeatmap, setGeoHeatmap] = useState(null);
    const [unfreezeOpen, setUnfreezeOpen] = useState(false);
    const [stripeIntent, setStripeIntent] = useState(null);
    const display = useDisplayCurrency();

    // Live Exposure Heatmap — real per-country exposure/PnL from open
    // positions (GET /dashboard/geo-heatmap), keyed by ISO-2 code to match
    // AfricanPortfolioMap's country paths. Only countries with actual open
    // exposure get an entry; everything else stays "idle" (default fill).
    const activeHoldings = useMemo(() => {
        if (!geoHeatmap?.countries) return undefined;
        const map = {};
        for (const c of geoHeatmap.countries) {
            if (!c.exposure_kes) continue;
            map[c.code] = {
                status: "holding",
                ticker: c.top_symbol,
                allocationPct: c.share_pct,
                pnlKes: c.pnl_kes,
                exposureKes: c.exposure_kes,
                positions: c.positions,
            };
        }
        return map;
    }, [geoHeatmap]);

    useEffect(() => {
        if (!user) return;
        try {
            const raw = localStorage.getItem("md_stripe_intent");
            if (!raw) return;
            const parsed = JSON.parse(raw);
            if (parsed?.packageId) {
                setStripeIntent({ packageId: parsed.packageId, label: parsed.label || null });
                // Clear the intent immediately so a refresh doesn't re-open it.
                localStorage.removeItem("md_stripe_intent");
            }
        } catch { /* ignore */ }
    }, [user]);

    // Render a KES amount in the user's chosen display currency. When the
    // display currency is KES we defer to the existing fmtKES for pixel-
    // perfect back-compat with the legacy layout.
    const fmt = (kes) => (
        display.code === "KES"
            ? fmtKES(kes)
            : formatDisplayAmount(display.convert(kes), display.code, display.symbol)
    );

    async function load() {
        try {
            // PriceHistoryChart owns its own /market/daily fetch, so we no
            // longer need to preload /market/history here.
            const [{ data: m }, { data: t }, { data: geo }] = await Promise.all([
                api.get("/metrics"),
                api.get("/market/tickers"),
                api.get("/dashboard/geo-heatmap").catch(() => ({ data: null })),
            ]);
            setMetrics(m);
            setTickers(t.tickers);
            if (geo) setGeoHeatmap(geo);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") console.debug("Dashboard load failed:", e);
        }
    }

    useEffect(() => {
        load();
        // WS handles fine-grained ticks & PnL. This 30s poll is a safety net that also
        // refreshes derived metrics (win rate, drawdown limits) which don't come over WS.
        const id = setInterval(load, 30000);
        return () => clearInterval(id);
    }, []);

    // Real-time PnL + tickers via WebSocket — auth via httpOnly cookie (auto-sent on same-origin WS).
    useMarketSocket({
        onTicks: (list) => {
            setTickers((prev) => {
                if (!prev.length) return list.map((t) => ({ ...t, name: t.symbol, sector: "" }));
                const bySym = Object.fromEntries(list.map((t) => [t.symbol, t]));
                return prev.map((p) => bySym[p.symbol] ? { ...p, ...bySym[p.symbol] } : p);
            });
        },
        onPnl: (snap) => {
            setMetrics((prev) => prev ? { ...prev, ...snap } : prev);
        },
    });

    async function downloadExport(path, mime) {
        try {
            const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
                credentials: "include", // Cookie carries the auth token.
            });
            if (!resp.ok) throw new Error("Export failed");
            const blob = await resp.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = path.split("/").pop();
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (e) {
            if (process.env.NODE_ENV !== "production") console.debug("Export download failed:", e);
            toast.error("Download failed. Please try again.");
        }
    }

    if (!metrics) return (
        <div className="space-y-6" data-testid="dashboard-loading">
            <SkeletonGrid variant="cards" count={4} />
            <SkeletonGrid variant="cards" count={3} />
        </div>
    );

    const dailyDDPct = Math.min(100, (metrics.daily_drawdown_used / metrics.daily_drawdown_limit) * 100);
    const maxDDPct = Math.min(100, (metrics.max_drawdown_used / metrics.max_drawdown_limit) * 100);
    const targetPct = Math.min(100, Math.max(0, (metrics.equity - metrics.starting_balance) / (metrics.profit_target - metrics.starting_balance) * 100));

    const tierBadge = (() => {
        const t = user?.tier;
        if (t === "moe_institution") return "MoE Institution";
        if (t === "cma_institution") return "CMA Institution";
        if (t === "advanced") return "Obondo Advanced";
        return "Individual";
    })();

    // ── Unified layout engine — every functional block below the KPI row is
    //    a draggable / resizable / minimizable / removable grid widget.
    const progressBody = (label, term, tooltip, editor, used, limit, pct, extra = null) => (
        <>
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-1.5">
                    <InfoTooltip term={term}>{tooltip}</InfoTooltip>
                    {editor}
                </div>
                <span className="mono text-xs font-semibold proportional-nums">{used} / {limit}</span>
            </div>
            <Progress value={pct} data-testid={`${label}-progress`} />
            {extra}
        </>
    );

    const traderWidgets = [
        { id: "geo-heatmap", title: "Pan-African Exposure", category: "Portfolio", layout: { x: 0, y: 0, w: 12, h: 9 }, minW: 4, minH: 5, noPadding: true, render: () => <AfricanPortfolioMap activeHoldings={activeHoldings} /> },
        { id: "open-positions", title: "Open Positions", category: "Portfolio", layout: { x: 0, y: 9, w: 12, h: 5 }, minW: 4, minH: 3, noPadding: true, render: () => <DashboardOpenPositions /> },
        {
            id: "progress-daily-dd", title: "Daily Drawdown", category: "Risk", layout: { x: 0, y: 18, w: 4, h: 3 }, minW: 3, minH: 3,
            render: () => progressBody(
                "daily-dd", "Drawdown",
                "Daily drawdown is measured from the higher of your daily starting balance or equity. Breach = frozen account.",
                <DrawdownEditor accountId={metrics.account_id || metrics.id} currentPct={metrics.daily_drawdown_pct} mode="daily" onSaved={() => load()} />,
                fmtKES(metrics.daily_drawdown_used), fmtKES(metrics.daily_drawdown_limit), dailyDDPct,
                <DailyResetChip />,
            ),
        },
        {
            id: "progress-max-dd", title: "Max Drawdown", category: "Risk", layout: { x: 4, y: 18, w: 4, h: 3 }, minW: 3, minH: 3,
            render: () => progressBody(
                "max-dd", "Max Drawdown",
                "Fixed limit set as a percentage of your starting balance. Default 10%. Not trailing.",
                <DrawdownEditor accountId={metrics.account_id || metrics.id} currentPct={metrics.max_drawdown_pct} mode="max" onSaved={() => load()} />,
                fmtKES(metrics.max_drawdown_used), fmtKES(metrics.max_drawdown_limit), maxDDPct,
            ),
        },
        {
            id: "progress-target", title: "Profit Target", category: "Risk", layout: { x: 8, y: 18, w: 4, h: 3 }, minW: 3, minH: 3,
            render: () => progressBody(
                "profit-target", "Profit Target",
                "The profit level you must reach to pass evaluation. Default 8% of starting balance; you can customise 1% – 50%.",
                <ProfitTargetEditor accountId={metrics.account_id || metrics.id} currentPct={metrics.profit_target_pct} onSaved={() => load()} />,
                "Target", fmtKES(metrics.profit_target), targetPct,
            ),
        },
        { id: "closed-trades", title: "Closed Trades", category: "Portfolio", layout: { x: 0, y: 21, w: 12, h: 6 }, minW: 6, minH: 4, noPadding: true, render: () => <ClosedTradesCard /> },
        { id: "recent-activity", title: "Recent Activity", category: "Activity", layout: { x: 0, y: 27, w: 8, h: 9 }, minW: 3, minH: 5, noPadding: true, render: () => <RecentActivityFeed /> },
        { id: "debrief-insights", title: "Debrief Insights", category: "Coaching", layout: { x: 0, y: 36, w: 6, h: 9 }, minW: 3, minH: 5, noPadding: true, render: () => <DebriefInsightsCard /> },
    ];

    return (
        <>
            {/* Breach alert stays at the very top for maximum visibility */}
            {(metrics.breached || user?.account_status === "frozen") && (
                <div className="mb-4 flex items-start gap-3 border border-rose-500/40 bg-rose-500/5 rounded-xl p-4" data-testid="breach-alert">
                    <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0 mt-0.5" />
                    <div className="text-sm flex-1">
                        <div className="font-semibold text-rose-500">Account frozen — drawdown limit breached.</div>
                        <div className="text-muted-foreground">
                            You've hit your daily or max drawdown limit. Reopen your account yourself — no support ticket needed.
                        </div>
                    </div>
                    <Button data-testid="open-self-unfreeze" variant="destructive" size="sm" onClick={() => setUnfreezeOpen(true)}>
                        Reopen my account →
                    </Button>
                </div>
            )}

            <SelfUnfreezeModal open={unfreezeOpen} onClose={() => setUnfreezeOpen(false)} onUnfrozen={load} />

            {/* Post-signup restore — if the user picked a tier on Landing
                while anonymous, resume that Stripe checkout as soon as
                they land on their dashboard. */}
            <StripeCheckoutModal
                open={Boolean(stripeIntent)}
                packageId={stripeIntent?.packageId || "institutional_monthly"}
                tierLabel={stripeIntent?.label}
                onClose={() => setStripeIntent(null)}
            />

            {/* Baseline nudge — subtle chip for signed-in users without a
                psychometric baseline. Auto-hides once completed or when
                dismissed for the current 12h window. Hidden while an
                earlier onboarding stage is still active (sequenced below). */}
            {stage !== "profile" && stage !== "strategy" && (
                <PsychometricBaselineNudge locked={stage === "psychometric"} />
            )}

            {/* Directive 4 Amendment · Obondo 7-day trial banner (sticky). */}
            {stage === "complete" && <ObondoTrialBanner />}
            {/* Directive 12 · pre-tilt breakdown-pattern warning banner (sticky). */}
            {stage === "complete" && <BreakdownPatternBanner />}

            {/* PageHeader — dense, consistent H1 with actions */}
            <PageHeader
                eyebrow={tApp("greeting.eyebrow", "Trader dashboard")}
                title={
                    <>
                        {tApp("greeting.hello", "Hello, {name}.").split("{name}")[0]}
                        <bdi>{user?.full_name?.split(" ")[0] || tApp("greeting.trader", "trader")}</bdi>
                        {tApp("greeting.hello", "Hello, {name}.").split("{name}")[1] || ""}
                    </>
                }
                subtitle={
                    <span className="inline-flex items-center gap-1.5 flex-wrap">
                        <StatusBadge tone="info">{tierBadge}</StatusBadge>
                        {tApp("greeting.referral", "Referral:")} <span className="mono font-bold text-foreground" data-testid="dash-referral-code">{user?.referral_code}</span>
                        {user?.referral_code && (
                            <CopyButton value={user.referral_code} toastMsg={tApp("greeting.referral_copied", "Referral code copied")} iconOnly testId="dash-copy-referral" className="h-6 px-1.5" />
                        )}
                    </span>
                }
                actions={
                    <>
                        <Link to="/app/terminal"><Button size="sm" data-testid="dash-open-terminal">{tApp("greeting.open_terminal", "Open Terminal")}</Button></Link>
                        <Button size="sm" variant="outline" onClick={() => downloadExport("/api/export/trades.csv", "text/csv")} data-testid="export-trades-csv" title="Download closed trades as CSV">
                            <Download className="w-3.5 h-3.5 mr-1" /> CSV
                        </Button>
                    </>
                }
            />

            {/* ─────────────────────────────────────────────────────────────
                ABOVE THE FOLD
                Row 1: Get set up — profile completion widget
                Row 2: 4 KPI cards — reorderable via dnd-kit (Round 66)
                Below: unified react-grid-layout canvas — drag / resize /
                minimize / delete every functional block (Round 67).

                Progressive onboarding lock (2026-09-05): while `stage` is
                anything other than "complete", the questionnaire matching
                the active stage is shown WITHOUT a dismiss option, and
                everything below it (KPI row + the full widget canvas) is
                visually locked — greyed out, non-interactive — until it's
                cleared. Sidebar/MobileBottomNav enforce the same lock on
                navigation; `OnboardingGateRedirect` enforces it on direct
                URL entry.
               ───────────────────────────────────────────────────────────── */}
            <ProfileCompletionWidget />
            {stage !== "profile" && (
                <InvestmentStrategyNudge locked={stage === "strategy"} />
            )}

            <div className="relative">
                <div
                    className={stage !== "complete" ? "relative pointer-events-none select-none blur-sm opacity-20" : ""}
                    aria-disabled={stage !== "complete" ? "true" : undefined}
                    data-testid="dashboard-locked-region"
                    data-locked={stage !== "complete" ? "true" : "false"}
                >
                    <KpiSortableRow
                        userId={user?.id}
                        metrics={metrics}
                        fmt={fmt}
                        display={display}
                        isInstitutional={isInstitutional}
                        onReload={() => load()}
                    />

                    <DashboardGridWrapper
                        role="TRADER"
                        viewId="trader-main"
                        widgets={traderWidgets}
                        className="mb-3"
                    />
                </div>

                {/* Fully opaque scrim — the blurred/dimmed region above is no
                    longer readable through it (fixed a reported bug where the
                    faint underlying numbers/text bled through the lock). */}
                {stage !== "complete" && (
                    <div
                        className="absolute inset-0 z-10 bg-background/95 backdrop-blur-md flex items-start justify-center pt-10"
                        data-testid="dashboard-lock-banner"
                    >
                        <div className="inline-flex items-center gap-2 rounded-full border border-primary/40 bg-primary/10 px-4 py-2 text-xs font-bold text-primary shadow-sm">
                            <Lock className="w-3.5 h-3.5" />
                            Complete the step above to unlock your dashboard
                        </div>
                    </div>
                )}
            </div>
        </>
    );
}
